import { defineComponent as _defineComponent } from 'vue'
import { ref as _ref, computed as _computed } from 'vue';

import { computed } from 'vue';
import {
  AssetPatchDto,
  CancelablePromise,
  RuleCatalogAssetDtoV2,
  RuleService,
  TagDto,
  type EntityCustomMetadataDto,
} from '@/api';
import ModalDialog from '@/components/modal-dialog/Modal-Dialog.vue';
import type { RulePatchDto } from '@/api/models/RulePatchDto';
import { createPatchDto } from '@/api/patches';
import i18n from '@/i18n';
import { usePlans } from '@/composables/plans';
import { AssetsTypesEnum } from '@/components/custom-metadata/CustomMetadata';
import CustomMetadataSelector from '@/components/selectors/Custom-Metadata-Selector/CustomMetadataSelector.vue';



type MonitorsEditEmits = {
  (event: 'update'): void
}


enum Edits {
  Schedule = 'schedule',
  Tags = 'tags',
  BusinessTerms = 'businessTerms',
}

export default /*#__PURE__*/_defineComponent({
  __name: 'Monitors-Edit',
  emits: ["update"],
  setup(__props, { expose, emit }) {
const EDIT_PER_PLATFORM: Record<RuleCatalogAssetDtoV2.sourcePlatform, Edits[]> = {
  [RuleCatalogAssetDtoV2.sourcePlatform.DBT]: [Edits.Tags, Edits.BusinessTerms],
  [RuleCatalogAssetDtoV2.sourcePlatform.SIFFLET]: [Edits.Schedule, Edits.Tags, Edits.BusinessTerms],
};



const modalDialogRef = _ref<InstanceType<typeof ModalDialog> | null>(null);
const valid = _ref(false);

const plans = usePlans();
// Plan is already loaded by parent MonitorResults component
const minMonitorPeriodSeconds = _computed(() => plans.getMonitoringPlan.value?.minMonitorPeriodSeconds);

let tags = _ref<TagDto[] | undefined>([]);
let terms = _ref<TagDto[]>([]);
let criticality = _ref<number | null>(null);
let removeExistingTags = _ref(false);
let removeExistingTerms = _ref(false);
const removeExistingSchedule = _ref(false);
/** null = mixed bulk state (indeterminate); explicit boolean reflects consensus or user choice. */
let createIncidentOnFail = _ref<boolean | null>(null);
let isOverridingNotificationRules = _ref<boolean | null>(null);
let createIncidentOnFailDirty = _ref(false);
let isOverridingNotificationRulesDirty = _ref(false);
let schedule = _ref('');
let monitors = _ref<RuleCatalogAssetDtoV2[]>([]);
let sourcePlatforms = _ref<RuleCatalogAssetDtoV2.sourcePlatform[] | []>([]);
let customMetadata = _ref<EntityCustomMetadataDto[]>([]);

const fields = _ref([
  'tags',
  'terms',
  'schedule',
  'customMetadata',
]);

const isSingleEdit = _computed(() => monitors.value.length === 1);
const monitorsCount = computed<number>(() => monitors.value.length);
const editingRules = _computed(() => sourcePlatforms.value.map((platform) => EDIT_PER_PLATFORM[platform]).flat());
const isEditableSchedule = _computed(() => editingRules.value.some((rule) => rule === Edits.Schedule));

const hasNonEditableScheduleRule = _computed(() => {
  const newEditingRules = sourcePlatforms.value.map((platform) => EDIT_PER_PLATFORM[platform]);
  return newEditingRules.some((rules) => !rules.includes(Edits.Schedule));
});

const scheduleInputHint = computed<string>(() => (hasNonEditableScheduleRule.value
  ? i18n.t('monitors.dbt_schedule_cant_be_edited')
  : ''));

const createIncidentCheckboxLabel = computed<string>(() => (
  isSingleEdit.value
    ? i18n.t('edit-dialog.create_incident')
    : i18n.t('edit-dialog.create_incidents_when_monitors_fail')
));

const resolveBulkCheckboxValue = (values: boolean[]): boolean | null => {
  if (values.length === 0) {
    return null;
  }
  const [firstValue] = values;
  return values.every((value) => value === firstValue) ? firstValue : null;
};

const isCreateIncidentOnFailIndeterminate = _computed(
  () => !isSingleEdit.value && createIncidentOnFail.value === null,
);

const isOverridingNotificationRulesIndeterminate = _computed(
  () => !isSingleEdit.value && isOverridingNotificationRules.value === null,
);

const handleCreateIncidentOnFailChange = (value: boolean) => {
  createIncidentOnFail.value = value;
  createIncidentOnFailDirty.value = true;
};

const handleIsOverridingNotificationRulesChange = (value: boolean) => {
  isOverridingNotificationRules.value = value;
  isOverridingNotificationRulesDirty.value = true;
};

const getNotificationMethodPatchFields = (): Partial<Pick<RulePatchDto, 'createIncidentOnFail' | 'isOverridingNotificationRules'>> => {
  if (isSingleEdit.value) {
    return {
      createIncidentOnFail: createIncidentOnFail.value ?? true,
      isOverridingNotificationRules: isOverridingNotificationRules.value ?? false,
    };
  }

  const patch: Partial<Pick<RulePatchDto, 'createIncidentOnFail' | 'isOverridingNotificationRules'>> = {};
  if (createIncidentOnFailDirty.value && createIncidentOnFail.value !== null) {
    patch.createIncidentOnFail = createIncidentOnFail.value;
  }
  if (isOverridingNotificationRulesDirty.value && isOverridingNotificationRules.value !== null) {
    patch.isOverridingNotificationRules = isOverridingNotificationRules.value;
  }
  return patch;
};

const getPatch = (currentState: RuleCatalogAssetDtoV2) => {
  let tagIds: any = [];
  let termIds: any = [];
  let newSchedule = currentState.schedule;
  let customMetadataEntryIds: string[] = [];

  fields.value.forEach((field) => {
    switch (field) {
      case 'tags':
        tagIds = createPatchDto({ tags: tags.value }, currentState.tags, removeExistingTags.value, isSingleEdit.value);
        break;
      case 'terms':
        termIds = createPatchDto({ terms: terms.value }, currentState.terms, removeExistingTerms.value, isSingleEdit.value);
        break;
      case 'schedule':
        if (removeExistingSchedule.value && !schedule.value) newSchedule = '';
        if (isSingleEdit.value || schedule.value) newSchedule = schedule.value;
        break;
      case 'customMetadata':
        customMetadataEntryIds = customMetadata.value?.flatMap((metadata) => metadata.entries.map((entry) => entry.id)) || [];
        break;
      default:
        break;
    }
  });

  // If more than one monitor, apply the patch 'ADD' method to custom metadata entries
  const patchOperation = isSingleEdit.value ? AssetPatchDto.customMetadataEntriesOperation.REPLACE : AssetPatchDto.customMetadataEntriesOperation.ADD;

  return {
    tagIds,
    termIds,
    schedule: newSchedule,
    ...getNotificationMethodPatchFields(),
    criticality: (criticality.value || criticality.value === 0) ? criticality.value : currentState.criticality,
    customMetadataEntryIds,
    customMetadataEntriesOperation: patchOperation,
  } as RulePatchDto;
};

const init = () => {
  monitors.value = [];
  tags.value = [];
  terms.value = [];
  schedule.value = '';
  createIncidentOnFail.value = null;
  isOverridingNotificationRules.value = null;
  createIncidentOnFailDirty.value = false;
  isOverridingNotificationRulesDirty.value = false;
  removeExistingTags.value = false;
  removeExistingTerms.value = false;
  criticality.value = null;
  customMetadata.value = [];
};

const open = () => modalDialogRef.value!.openDialog();
const close = () => modalDialogRef.value!.closeDialog();

const update = async () => {
  const patches: RulePatchDto[] = [];
  let promises: CancelablePromise<any>[] = [];

  monitors.value.forEach((monitor) => patches.push(getPatch(monitor)));

  if (Array.isArray(monitors.value) && Array.isArray(patches)) {
    promises = monitors.value.map((monitor, index) => {
      const rulePatch = patches[index];
      return RuleService.patchRule({ id: monitor.id, requestBody: rulePatch });
    });
  } else {
    const requestBody = patches as RulePatchDto;
    monitors.value.map((monitor) => RuleService.patchRule({ id: monitor.id, requestBody }));
  }

  await Promise.all(promises);
  close();
  emit('update');
};

const setMonitors = async (newMonitors: RuleCatalogAssetDtoV2[]) => {
  init();
  monitors.value = newMonitors;
  sourcePlatforms.value = monitors.value.map(({ sourcePlatform }) => sourcePlatform);

  const selectedMonitorRules = await Promise.all(
    newMonitors.map(({ id }) => RuleService.getSiffletRuleById({ id })),
  );
  const monitor = selectedMonitorRules[0];

  if (isSingleEdit.value) {
    if (monitor.tags) tags.value = monitor.tags;
    if (monitor.terms) terms.value = monitor.terms;
    if (monitor.schedule) schedule.value = monitor.schedule;
    createIncidentOnFail.value = monitor.createIncidentOnFail ?? true;
    isOverridingNotificationRules.value = monitor.isOverridingNotificationRules ?? false;
    if (monitor.criticality) criticality.value = monitor.criticality;
    if (monitor.customMetadata) {
      customMetadata.value = monitor.customMetadata;
    } else {
      customMetadata.value = [];
    }
  } else {
    createIncidentOnFail.value = resolveBulkCheckboxValue(
      selectedMonitorRules.map((rule) => rule.createIncidentOnFail ?? true),
    );
    isOverridingNotificationRules.value = resolveBulkCheckboxValue(
      selectedMonitorRules.map((rule) => rule.isOverridingNotificationRules ?? false),
    );
    // Preserve cusstom metadata structure but empty all entries
    customMetadata.value = monitor?.customMetadata?.map((metadata) => ({
      ...metadata,
      entries: [],
    })) || [];
  }

  open();
};

const updateCriticality = (newCriticality: number) => {
  criticality.value = newCriticality;
};

expose({ setMonitors });

return { __sfc: true,Edits, EDIT_PER_PLATFORM, emit, modalDialogRef, valid, plans, minMonitorPeriodSeconds, tags, terms, criticality, removeExistingTags, removeExistingTerms, removeExistingSchedule, createIncidentOnFail, isOverridingNotificationRules, createIncidentOnFailDirty, isOverridingNotificationRulesDirty, schedule, monitors, sourcePlatforms, customMetadata, fields, isSingleEdit, monitorsCount, editingRules, isEditableSchedule, hasNonEditableScheduleRule, scheduleInputHint, createIncidentCheckboxLabel, resolveBulkCheckboxValue, isCreateIncidentOnFailIndeterminate, isOverridingNotificationRulesIndeterminate, handleCreateIncidentOnFailChange, handleIsOverridingNotificationRulesChange, getNotificationMethodPatchFields, getPatch, init, open, close, update, setMonitors, updateCriticality, _ref, _computed, computed, AssetPatchDto, CancelablePromise, RuleCatalogAssetDtoV2, RuleService, TagDto, ModalDialog, createPatchDto, i18n, usePlans, AssetsTypesEnum, CustomMetadataSelector }
}

})